import React, { FC, useEffect, useState } from 'react';

import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import { Fit, Layout, useRive } from '@rive-app/react-canvas';
import { OrderShort } from '@solidgate/react-sdk';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import Api from '~/Api';
import Loader from '~/components/atoms/Loader';
import PaymentFormV2 from '~/components/organisms/SolidCheckout/components/PaymentForm/V2';
import { UpdatePaymentTokenReason } from '~/components/organisms/SolidCheckout/types';
import {
  DEFAULT_DISCOUNT_VALUE,
  FREE_AMOUNT_OF_DAYS,
  getFormParams,
  PrepaidBenefits,
} from '~/modules/prepaid/constants';
import projectConfig from '~/projects/config';
import analytics from '~/services/analytics';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { Typographies } from '~/theme/typography';
import { AnalyticPaymentMethods, PAYMENT_METHODS } from '~/types/payment';
import { onAssetLoad } from '~/utils/animations';

import styles from './styles.module.scss';
import PrepaidCardsSrc from '../../assets/prepaid_cards.riv';

const getTexts = (
  t: TFunction,
): Record<
  PrepaidBenefits,
  {
    runName: string;
    text: string;
  }[]
> => {
  const commonTexts = [
    {
      runName: 'NonPrepaid',
      text: t('modal:non_prepaid.animation_text[0]', 'Non-Prepaid'),
    },

    {
      runName: 'Add',
      text: t('modal:non_prepaid.animation_text[1]', 'Add'),
    },
  ];

  return {
    [PrepaidBenefits.EXTRA_WEEK_TRIAL]: [
      {
        runName: 'offer',
        text: t('modal:non_prepaid.animation_text[2]', {
          amount: FREE_AMOUNT_OF_DAYS,
          defaultValue: 'Extra {{amount}} Days',
        }),
      },
      ...commonTexts,
    ],
    [PrepaidBenefits.DISCOUNT_35]: [
      {
        runName: 'offer',
        text: t('modal:non_prepaid.animation_text[3]', {
          discount_percentage: DEFAULT_DISCOUNT_VALUE,
          defaultValue: '-{{discount_percentage}}% Discount',
        }),
      },
      ...commonTexts,
    ],
  };
};

const getBenefits = (t: TFunction): { key: string; text: string }[] => {
  return [
    {
      key: 'b1',
      text: t(
        'modal:non_prepaid.benefits_text[0]',
        'Ensure Continuous Access To All Features',
      ),
    },
    {
      key: 'b2',
      text: t('modal:non_prepaid.benefits_text[1]', {
        days_amount: 7,
        hours_amount: 423,
        project: projectConfig.getProjectName(),
        defaultValue:
          'In {{days_amount}} Days, Using {{project}} Saves On Average {{hours_amount}} Hours',
      }),
    },
    {
      key: 'b3',
      text: t('modal:non_prepaid.benefits_text[2]', {
        percent_amount: 92,
        project: projectConfig.getProjectName(),
        months_amount: 6,
        defaultValue:
          '{{percent_amount}}% of {{project}} users stay with us for more than {{months_amount}} months',
      }),
    },
  ];
};

type Props = {
  benefit: PrepaidBenefits;
  eventProperties: Record<string, string>;
  onSuccess: (order: OrderShort) => void | Promise<void>;
};

const NonPrepaidModal: FC<Props> = ({
  benefit,
  eventProperties,
  onSuccess,
}) => {
  const profile = useAppSelector((state) => state.profile);
  const { t } = useTranslation();

  const [isFormReady, setIsFormReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { rive, RiveComponent } = useRive({
    src: PrepaidCardsSrc,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
    }),
    assetLoader: onAssetLoad,
  });

  const handlePaymentMethodChange = (type: PAYMENT_METHODS): void => {
    analytics.trackEvent('platform checkout - navigation', {
      method: type,
      ...eventProperties,
    });
  };

  const handlePaymentSuccess = async (order: OrderShort): Promise<void> => {
    analytics.trackEvent('platform checkout - payment update success', {
      subscription_id: profile.user_subscription.id,
      ...eventProperties,
    });

    await onSuccess(order);
    setIsLoading(false);
  };

  const handlePaymentFail = (
    message: string,
    method: AnalyticPaymentMethods,
  ): void => {
    analytics.trackEvent('platform checkout - error', {
      method,
      error: message,
      ...eventProperties,
    });
    setIsLoading(false);
  };

  const handlePaymentSubmit = (method: AnalyticPaymentMethods): void => {
    analytics.trackEvent('platform checkout - submit', {
      method,
      ...eventProperties,
    });
    setIsLoading(true);
  };

  const handlePaymentView = (): void => {
    analytics.trackEvent(
      'platform checkout - payment update view',
      eventProperties,
    );
  };

  useEffect(() => {
    if (rive) {
      getTexts(t)[benefit].forEach(({ runName, text }) => {
        rive.setTextRunValue(runName, text);
      });
    }
  }, [rive]);

  return (
    <>
      {(isLoading || !isFormReady) && <Loader />}

      <div
        className={clsx(styles.container, {
          [styles.is_shown]: !isLoading && isFormReady,
        })}
      >
        <div className={styles.intro}>
          {isFormReady && (
            <>
              <Typography
                variant={Typographies.TITLE_LARGE}
                className={styles.intro__title}
                component="div"
              >
                {benefit === PrepaidBenefits.EXTRA_WEEK_TRIAL
                  ? t('modal:non_prepaid.title[0]', {
                      amount: FREE_AMOUNT_OF_DAYS,
                      defaultValue:
                        'Add a non-prepaid card to use Justdone for {{amount}} more days for free',
                    })
                  : t('modal:non_prepaid.title[1]', {
                      percent: DEFAULT_DISCOUNT_VALUE,
                      defaultValue:
                        'Add a non-prepaid card for a {{percent}}% discount on your Justdone plan',
                    })}
              </Typography>

              <div className={styles.intro__animation}>
                <RiveComponent />
              </div>

              <div className={styles.intro__benefits}>
                {getBenefits(t).map(({ key, text }) => {
                  return (
                    <Typography
                      variant={Typographies.BODY_MEDIUM}
                      className={styles.intro__benefits_item}
                      key={key}
                      component="div"
                    >
                      <Icon icon="material-symbols:check" width="20" />

                      <span>{text}</span>
                    </Typography>
                  );
                })}
              </div>

              <Typography
                variant={Typographies.BODY_SMALL}
                className={styles.intro__disclaimer}
                component="div"
              >
                {t(
                  'modal:non_prepaid.disclaimer',
                  'By adding this payment method, you authorize its use for your subscription payments',
                )}
              </Typography>
            </>
          )}
        </div>

        <div className={styles.form}>
          <PaymentFormV2
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentFail={handlePaymentFail}
            onPaymentMethodChange={handlePaymentMethodChange}
            onPaymentSubmit={handlePaymentSubmit}
            onPaymentView={handlePaymentView}
            onFormReady={() => setIsFormReady(true)}
            paymentApi={{
              createPaymentIntentCard: (geo_country) =>
                Api.updatePaymentMethodCard({
                  geo_country,
                  token_type: UpdatePaymentTokenReason.PREPAID,
                }),
            }}
            formParams={{
              submitButtonText:
                benefit === PrepaidBenefits.EXTRA_WEEK_TRIAL
                  ? t('general:get_free_days', {
                      amount: FREE_AMOUNT_OF_DAYS,
                      defaultValue: 'Get Free {{amount}} Days',
                    })
                  : t('general:save_percent', {
                      percent: DEFAULT_DISCOUNT_VALUE,
                      defaultValue: 'Save {{percent}}%',
                    }),
              ...getFormParams(t),
            }}
            SummaryComponent={() => (
              <Typography
                variant={Typographies.TITLE_MEDIUM}
                className={styles.pricing}
                component="div"
              >
                {t('general:total', 'Total')}: $0
              </Typography>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default NonPrepaidModal;
